import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { formatCents } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { addressLines, HOLD_DAYS, methodLabel, PAYOUT_METHODS, type Affiliate, type Payout } from "./affiliates";

interface PayoutPreview {
  affiliate?: string;
  pay?: string;
  commissions?: number;
  method?: string;
  note?: string;
}

/** payouts_record (money): records a payment Brandon already sent and marks the payable commissions paid.
 *  First call without confirm shows the server's preview; confirm=true records it. */
export function PayoutDialog({ affiliate, name, onClose }: { affiliate: Affiliate; name: string; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [method, setMethod] = useState(affiliate.payout_method ?? "");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState<PayoutPreview | null>(null);
  const [problem, setProblem] = useState("");
  const body = (confirm: boolean) => ({
    affiliate_id: affiliate.id,
    method: method || undefined,
    reference: reference.trim() || undefined,
    note: note.trim() || undefined,
    confirm: confirm || undefined,
  });
  const review = useMutation({
    mutationFn: async () => {
      try {
        await api("/api/v1/payouts", { json: body(false) });
        throw new Error("The server didn't ask for a confirmation. Nothing was recorded. Tell crm-billing.");
      } catch (e) {
        if (e instanceof ApiError && e.code === "confirm_required") return (e.details ?? {}) as PayoutPreview;
        throw e;
      }
    },
    onMutate: () => setProblem(""),
    onSuccess: setPreview,
    onError: (e) => setProblem(e.message),
  });
  const record = useMutation({
    mutationFn: () => api<Payout>("/api/v1/payouts", { json: body(true) }),
    onSuccess: (p) => {
      for (const k of ["affiliates", "affiliate", "affiliate-commissions", "payouts"]) void qc.invalidateQueries({ queryKey: [k] });
      toast(`Recorded a ${formatCents(p.amount_cents)} payout to ${affiliate.code}`);
      onClose();
    },
    onError: (e) => setProblem(e.message),
  });
  const nothing = preview && (preview.commissions ?? 0) === 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(540px,calc(100vw-2rem))]">
        <DialogTitle>{preview ? "Check before recording" : `Record a payout to ${name}`}</DialogTitle>
        {!preview ? (
          <>
            <DialogDescription>
              This doesn't send money. Send it first (check, PayPal or bank transfer), then record it here: it pays everything payable now ({formatCents(affiliate.payable_cents)}:
              commissions older than {HOLD_DAYS} days, minus refunds) and marks those commissions paid.
            </DialogDescription>
            <div className="grid gap-3 text-sm sm:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="po-method">Paid by</Label>
                <Select id="po-method" value={method} onChange={(e) => setMethod(e.target.value)}>
                  {!affiliate.payout_method && <option value="">Not set</option>}
                  {PAYOUT_METHODS.map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="po-ref">Check number or reference</Label>
                <Input id="po-ref" value={reference} onChange={(e) => setReference(e.target.value)} />
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label htmlFor="po-note">Note (optional)</Label>
                <Input id="po-note" value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
            </div>
          </>
        ) : (
          <>
            <DialogDescription>{nothing ? "There's nothing to pay right now." : "This is what will be recorded."}</DialogDescription>
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
              <dt className="text-[var(--color-muted-foreground)]">Partner</dt>
              <dd>
                {name} ({preview.affiliate})
              </dd>
              <dt className="text-[var(--color-muted-foreground)]">Amount</dt>
              <dd className="font-semibold">{preview.pay}</dd>
              <dt className="text-[var(--color-muted-foreground)]">Commissions</dt>
              <dd>{preview.commissions ?? 0} settled (refund take-backs included)</dd>
              <dt className="text-[var(--color-muted-foreground)]">Paid by</dt>
              <dd>
                {methodLabel(preview.method)}
                {reference.trim() ? `, ${reference.trim()}` : ""}
              </dd>
              {preview.method === "check" && (
                <>
                  <dt className="text-[var(--color-muted-foreground)]">Mail to</dt>
                  <dd>
                    {addressLines(affiliate.payout_address).length
                      ? addressLines(affiliate.payout_address).map((l, i) => (
                          <span key={i} className="block">
                            {l}
                          </span>
                        ))
                      : "No mailing address on file"}
                  </dd>
                </>
              )}
            </dl>
            {!nothing && <p className="mt-2 text-xs text-[var(--color-warning-fg)]">Only record it once the money has actually gone out.</p>}
          </>
        )}
        {problem && (
          <p role="alert" className="text-sm text-[var(--color-danger-fg)]">
            {problem}
          </p>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={preview ? () => setPreview(null) : onClose}>
            {preview ? "Back" : "Cancel"}
          </Button>
          {!preview ? (
            <Button disabled={review.isPending} onClick={() => review.mutate()}>
              Review
            </Button>
          ) : (
            <Button disabled={record.isPending || !!nothing} onClick={() => record.mutate()}>
              Record {preview.pay} payout
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
